// src/components/shows/ShowForm.tsx
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { categoriesApi, type Category } from '../../api/categories';
import { showsApi, type Show } from '../../api/shows';
import { CategorySelector } from '../common/CategorySelector';
import { ImageUploader } from '../common/ImageUploader';
import { Button } from '../ui/button';
import { showFormSchema } from '../../validation/show';
import type { ShowFormValues } from '../../validation/show';

interface ShowFormProps {
  initialData?: Show | null;
  onSuccess?: (show: Show) => void;
  onCancel: () => void;
}

export function ShowForm({ initialData, onSuccess, onCancel }: ShowFormProps) {
  const [formData, setFormData] = useState<{
    name: string;
    hostName: string;
    description: string;
    isActive: boolean;
    categories: Category[];
    thumbnail: string;
    isSpecial: boolean;
    isFeatured: boolean
  }>({
    name: initialData?.name || '',
    hostName: initialData?.hostName || '',
    description: initialData?.description || '',
    isActive: initialData?.isActive ?? true,
    categories: initialData?.categories?.map(cat => ({
      id: cat.id,
      name: cat.name,
      isActive: cat.isActive,
      createdAt: (cat as any).createdAt || new Date().toISOString(),
      updatedAt: (cat as any).updatedAt || new Date().toISOString()
    })) || [],
    thumbnail: initialData?.thumbnail || '',
    isSpecial: initialData?.isSpecial ?? false,
    isFeatured: initialData?.isFeatured ?? false,
  });
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState(initialData?.thumbnail || '');
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>(
    initialData?.categories?.map(cat => ({
      id: cat.id,
      name: cat.name,
      isActive: cat.isActive,
      createdAt: cat.createdAt,
      updatedAt: cat.updatedAt
    })) || []
  );
  const [errors, setErrors] = useState<Partial<Record<keyof ShowFormValues, string>>>({});

  const fetchCategories = async () => {
    try {
      const res = await categoriesApi.getCategories({
        status: 'active'
      });
      if (res.success) {
        setAllCategories(res.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleFileChange = (file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
        setThumbnailFile(file);
      };
      reader.readAsDataURL(file);
      setErrors(prev => ({
        ...prev,
        thumbnail: ''
      }));
    } else {
      setThumbnailPreview('');
      setThumbnailFile(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formDataToSend = new FormData();
    const isEdit = Boolean(initialData?.id);

    const payloadForValidation = {
      ...formData,
      categories: formData.categories.map(cat => cat.id),
      thumbnail: thumbnailFile ?? formData.thumbnail ?? undefined
    };

    const result = showFormSchema.safeParse(payloadForValidation);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};

      result.error.issues.forEach(issue => {
        if (issue.path.length > 0 && typeof issue.path[0] === 'string') {
          fieldErrors[issue.path[0]] = issue.message;
        }
      });

      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    Object.entries(formData).forEach(([key, value]) => {

      if (key === 'isActive') {
        if (isEdit) {
          formDataToSend.append('isActive', value ? 'true' : 'false');
        }
        return;
      }

      if (key === 'categories') {
        (value as Category[]).forEach(category => {
          formDataToSend.append('categories[]', category.id);
        });
        return;
      }

      if (key === 'thumbnail') return;

      if (value !== null && value !== undefined) {
        formDataToSend.append(key, String(value));
      }
    });

    if (thumbnailFile) {
      formDataToSend.append('thumbnail', thumbnailFile);
    } else if (isEdit) {
      if (formData.thumbnail === "") {
        formDataToSend.append('thumbnail', "");
      } else if (formData.thumbnail) {
        formDataToSend.append('thumbnail', formData.thumbnail);
      }
    }
    setIsLoading(true);
    try {
      const res = isEdit
        ? await showsApi.updateShow(initialData!.id, formDataToSend)
        : await showsApi.createShow(formDataToSend);

      if (res.success) {
        toast.success(res.message);
        onSuccess?.(res.data as any);
      } else {
        toast.error(res.message);
      }

    } catch (error) {
      console.error(error);
      toast.error('Failed to save show');

    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (selectedItems: Category[]) => {
    setSelectedCategories(selectedItems);
    setFormData(prev => ({
      ...prev,
      categories: selectedItems
    }));
    setErrors(prev => ({
      ...prev,
      categories: ''
    }));
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Show Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"
            placeholder="Enter show name"
            disabled={isLoading}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Host Name</label>
          <input
            type="text"
            name="hostName"
            value={formData.hostName}
            onChange={handleInputChange}
            className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"
            placeholder="Enter host name"
            disabled={isLoading}
          />
          {errors.hostName && <p className="text-red-500 text-xs mt-1">{errors.hostName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"
            placeholder="Enter show description"
            disabled={isLoading}
          />
          {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
        </div>

        <CategorySelector
          categories={allCategories as Category[]}
          selectedCategories={selectedCategories}
          onCategoryChange={handleCategoryChange}
          disabled={isLoading}
        />
        {errors.categories && <p className="text-red-500 text-xs mt-1">{errors.categories}</p>}

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-4">Thumbnail</label>
          <ImageUploader
            value={thumbnailPreview}
            onChange={handleFileChange}
            disabled={isLoading}
            placeholder="Choose Thumbnail"
            className="mt-1"
          />
          {errors.thumbnail && <p className="text-red-500 text-xs mt-1">{errors.thumbnail}</p>}
        </div>

        <div className="flex items-center pt-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label
              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${formData.isActive
                ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
                : 'bg-black/20 border-white/10 text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
            >
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="w-4 h-4 rounded border-white/20 bg-black/20 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
                disabled={isLoading}
              />
              <span className="text-sm font-medium">Active</span>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <label
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${formData.isSpecial
              ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
              : 'bg-black/20 border-white/10 text-slate-400 hover:bg-white/5 hover:text-white'
              }`}
          >
            <input
              type="checkbox"
              id="isSpecial"
              name="isSpecial"
              checked={formData.isSpecial}
              onChange={handleInputChange}
              disabled={isLoading}
              className="w-4 h-4 rounded border-white/20 bg-black/20 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
            />
            <span className="text-sm font-medium">Special Event</span>
          </label>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <label
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${formData.isFeatured
              ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
              : 'bg-black/20 border-white/10 text-slate-400 hover:bg-white/5 hover:text-white'
              }`}
          >
            <input
              type="checkbox"
              id="isFeatured"
              name="isFeatured"
              checked={formData.isFeatured}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  isFeatured: e.target.checked
                }));
              }}
              disabled={isLoading}
              className="w-4 h-4 rounded border-white/20 bg-black/20 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
            />
            <span className="text-sm font-medium">Featured Event</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
        <Button
          type="button"
          onClick={onCancel}
          variant="outline"
          className="px-4 py-2 border border-white/10 bg-transparent hover:bg-white/5 text-white"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white"
          disabled={isLoading}
        >
          {isLoading
            ? 'Saving...'
            : initialData?.id
              ? 'Update Show'
              : 'Create Show'}
        </Button>
      </div>
    </form>
  );
}